// =============================================================================
// Perfis — o que cada perfil pode fazer.
//
// Permissão é DADO, não código: quem pode o quê se configura aqui, e o sistema
// obedece no próximo request. É o que evita `if (perfil === 'gerente')`
// espalhado, que quebra no primeiro caso novo.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import api, { mensagemDeErro } from '../services/api';

export default function Perfis() {
  const [perfis, setPerfis] = useState([]);
  const [permissoes, setPermissoes] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [marcadas, setMarcadas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      const [p, pm] = await Promise.all([
        api.get('/api/admin/perfis'),
        api.get('/api/admin/permissoes'),
      ]);
      setPerfis(p.data.perfis);
      setPermissoes(pm.data.permissoes);
      setErro('');
    } catch (e) { setErro(mensagemDeErro(e)); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const grupos = useMemo(() => {
    const m = {};
    permissoes.forEach((p) => { (m[p.grupo] = m[p.grupo] || []).push(p); });
    return m;
  }, [permissoes]);

  function abrir(perfil) {
    setSelecionado(perfil);
    setMarcadas(perfil.permissoes || []);
  }

  function alternar(nome) {
    setMarcadas((atual) => (atual.includes(nome)
      ? atual.filter((x) => x !== nome)
      : [...atual, nome]));
  }

  async function salvar() {
    if (salvando) return;
    setSalvando(true);
    try {
      await api.put(`/api/admin/perfis/${selecionado.id}/permissoes`, { permissoes: marcadas });
      await Swal.fire({
        icon: 'success', title: 'Permissões salvas',
        text: `Quem usa "${selecionado.nome}" já sente a mudança no próximo acesso.`,
        confirmButtonColor: '#EA6A08', timer: 2600,
      });
      setSelecionado(null);
      carregar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Não foi possível salvar', text: mensagemDeErro(e) });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <div className="mb-3">
        <h1 className="h4 fw-bold mb-1">Perfis e permissões</h1>
        <p className="text-muted small mb-0">
          Cada pessoa tem um perfil, e o perfil diz o que ela pode fazer. Muda aqui, vale no próximo acesso.
        </p>
      </div>

      {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

      <div className="row g-3">
        <div className="col-12 col-lg-4">
          {perfis.map((p) => (
            <div key={p.id}
                 className={`card mb-2 linha-clicavel ${selecionado?.id === p.id ? 'border-2' : ''}`}
                 style={selecionado?.id === p.id ? { borderColor: 'var(--primary)' } : undefined}
                 onClick={() => abrir(p)}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-semibold">{p.nome}</div>
                    <div className="text-muted" style={{ fontSize: '.78rem' }}>{p.descricao}</div>
                  </div>
                  <span className="badge badge-neutro">{p.usuarios} pessoa{p.usuarios === 1 ? '' : 's'}</span>
                </div>
                <div className="text-muted mt-2" style={{ fontSize: '.75rem' }}>
                  {p.permissoes.length} permissã{p.permissoes.length === 1 ? 'o' : 'es'}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="col-12 col-lg-8">
          {!selecionado ? (
            <div className="card">
              <div className="card-body text-center py-5 text-muted">
                <i className="bi bi-shield-lock display-6 d-block mb-2 opacity-50" />
                <p className="mb-0 small">Escolha um perfil ao lado para ver e editar o que ele permite.</p>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="h6 fw-bold mb-0">{selecionado.nome}</h2>
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelecionado(null)}>
                      Cancelar
                    </button>
                    <button className="btn btn-sm btn-primary-fnx" disabled={salvando} onClick={salvar}>
                      {salvando ? 'Salvando…' : 'Salvar'}
                    </button>
                  </div>
                </div>

                {Object.entries(grupos).map(([grupo, itens]) => (
                  <div key={grupo} className="mb-3">
                    <div className="text-muted fw-semibold text-uppercase mb-2"
                         style={{ fontSize: '.72rem', letterSpacing: '.05em' }}>{grupo}</div>
                    {itens.map((p) => (
                      <div key={p.nome} className="form-check">
                        <input className="form-check-input" type="checkbox" id={`p-${p.nome}`}
                               checked={marcadas.includes(p.nome)}
                               onChange={() => alternar(p.nome)} />
                        <label className="form-check-label small" htmlFor={`p-${p.nome}`}>
                          {p.descricao || p.nome}
                          <code className="ms-2" style={{ fontSize: '.72rem' }}>{p.nome}</code>
                        </label>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
